import { ElementRef, Injector, Renderer2, Type, Directive, OnInit, inject, OnDestroy, isDevMode } from '@angular/core';

import { ApModalHelperBase } from './ApModalHelperBase';
import { ApModalHelperFactoryBase } from './ApModalHelperFactoryBase';
import { stringifyClass } from 'webcommon/shared/object-utils-ng1520';
import { ApBaseClass } from 'webcommon/shared/ap-base-class-ng1520';

@Directive()
export abstract class ApBaseModalComponent<T, TData = unknown> extends ApBaseClass implements OnInit, OnDestroy {
  // For a component, this Injector is actually an instance of NodeInjector at runtime
  // which will have providers specific to this element or parents, and the providers in the root module Injector.
  // (https://github.com/angular/angular/blob/15.2.1/packages/core/src/render3/di.ts)
  protected elementInjector = inject(Injector);

  private readonly modalHelper: ApModalHelperBase<T> = this.elementInjector
    .get(ApModalHelperFactoryBase)
    .create<T>(this.elementInjector, (r) => {
      this.onHide(r);
    });

  // you must override this is in a child class to give a specific type to TData
  declare readonly modalData: TData;
  // use this instead if we want to require a child class to use modalData:
  // abstract readonly modalData: TData;

  public constructor(
    // todo: remove this unnecessary argument when there's time to refactor every constructor that is calling this
    // eslint-disable-next-line @angular-eslint/prefer-inject
    _elementInjector: Injector,
  ) {
    super();
    // Add a class here, so that flex styles could be applied to this element,
    // in case it needs to contain a scrollable container, which could require nested flex containers all the way down.
    // ex: making the .modal-body element scrollable
    // (theoretically this could be used for other situations as well, that don't have to do with scrolling)
    this.elementInjector
      .get<Renderer2>(Renderer2 as Type<Renderer2>)
      .addClass(this.elementInjector.get(ElementRef).nativeElement, 'ap-modal-component-host');
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- i want this to be generic
  protected getModalData<TDataInferred>(): TDataInferred {
    return this.modalHelper.modalData<TDataInferred>();
  }

  ngOnInit(): void {
    if (isDevMode() && this.getModalData() && !this.modalData) {
      // throw an error if we are not correctly setting modalData to handle breaking change.
      // Previously, it was set by this class.
      throw new Error(
        `'${stringifyClass(this)}' won't work correctly without setting modalData = this.getModalData().` +
          `Search for this.getModalData() for examples`,
      );
    }

    this.modalHelper.onInit();
  }

  override ngOnDestroy(): void {
    this.modalHelper.ngOnDestroy();
    super.ngOnDestroy();
  }

  // close the activeModal because the user is done and there is a successful result
  closeModal(result: T | PromiseLike<T>): void {
    this.modalHelper.emitAndClose(result);
  }

  // dismiss the activeModal (which rejects the promise)
  dismissModal(reason?: unknown): void {
    this.modalHelper.dismiss(reason);
  }

  // resolve the promise because there is a successful result, but don't close the modal
  // call when closing of modal is already done elsewhere
  // At the moment, this should ONLY be used as a result of an event from DialogRef.closed observable
  protected emitWithoutClose(result: T | PromiseLike<T>): void {
    this.modalHelper.emitWithoutClose(result);
  }

  protected onHide(reason: string): void {
    this.modalHelper.dismissWithoutClose(`Modal dismissed because of: '${reason}'`);
  }
}
